/**
 * Token Refresh Utility
 * Handles automatic JWT token refresh before expiration
 * Logs out user if refresh fails or token expires
 */

import { secureStorage } from './secureStorage';
import userGlobalConfig from '../constants/globalConfig';
import { client } from '../services/networking.service';
import ENDPOINTS from '../services/endPoints';

// Configuration
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // Refresh 5 minutes before expiry
const TOKEN_CHECK_INTERVAL = 60 * 1000; // Check every minute
const MAX_REFRESH_RETRIES = 3;

let refreshTimer: NodeJS.Timeout | null = null;
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Parse JWT token and extract payload
 */
function parseJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}

/**
 * Get token expiration time in milliseconds
 */
function getTokenExpiration(token: string): number | null {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }
  return payload.exp * 1000; // Convert to milliseconds
}

/**
 * Check if token needs refresh
 */
function shouldRefreshToken(token: string): boolean {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return false;
  }
  
  const now = Date.now();
  const timeUntilExpiry = expiration - now;
  
  // Refresh if token expires within the buffer time
  return timeUntilExpiry <= TOKEN_REFRESH_BUFFER && timeUntilExpiry > 0;
}

/**
 * Check if token is expired
 */
function isTokenExpired(token: string): boolean {
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return false;
  }
  return Date.now() >= expiration;
}

/**
 * Subscribe to token refresh
 */
function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

/**
 * Notify all subscribers of new token
 */
function notifySubscribers(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

/**
 * Perform logout and cleanup
 */
export function performLogout(): void {
  console.log('🔒 Performing logout due to token expiration/refresh failure');
  
  // Clear tokens
  secureStorage.removeItem(userGlobalConfig.TOKEN);
  secureStorage.removeItem(userGlobalConfig.USER_TOKEN);
  secureStorage.removeItem(userGlobalConfig.ADMIN_DETAILS);
  
  // Stop monitoring
  stopTokenMonitoring();
  
  // Redirect to login
  window.location.href = '/#/login';
}

/**
 * Refresh the JWT token
 */
async function refreshToken(retryCount = 0): Promise<string | null> {
  if (isRefreshing) {
    // If already refreshing, wait for the result
    return new Promise((resolve) => {
      subscribeTokenRefresh((token) => resolve(token));
    });
  }

  isRefreshing = true;

  try {
    const currentToken = secureStorage.getToken(userGlobalConfig.TOKEN);
    
    if (!currentToken) {
      console.warn('⚠️  No token found for refresh');
      performLogout();
      return null;
    }

    // The API allows refreshing a recently-expired token within a 7-day grace
    // period (same as the owner/host apps), so only bail out here if the token
    // is expired *beyond* that window — otherwise let the server decide.
    const expiration = getTokenExpiration(currentToken);
    const GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;
    if (expiration && Date.now() - expiration > GRACE_PERIOD_MS) {
      console.error('❌ Token expired beyond grace period, cannot refresh');
      performLogout();
      return null;
    }

    console.log('🔄 Refreshing JWT token...');

    // Call refresh endpoint
    const response = await client.post(ENDPOINTS.REFRESH_TOKEN, {}, {
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });

    // API responds { status, message, data: { token, expiresIn }, error }.
    // The response interceptor returns the full axios response, so the fresh
    // token is at response.data.data.token — not response.data.token.
    const newToken = response.data?.data?.token ?? response.data?.token;

    if (newToken) {

      // Store new token
      secureStorage.setItem(userGlobalConfig.TOKEN, newToken);
      
      console.log('✅ Token refreshed successfully');
      
      // Notify subscribers
      notifySubscribers(newToken);
      
      isRefreshing = false;
      return newToken;
    } else {
      throw new Error('No token in refresh response');
    }
  } catch (error: any) {
    console.error('❌ Token refresh failed:', error);
    
    // Retry logic
    if (retryCount < MAX_REFRESH_RETRIES) {
      console.log(`🔄 Retrying token refresh (${retryCount + 1}/${MAX_REFRESH_RETRIES})...`);
      isRefreshing = false;
      
      // Exponential backoff
      await new Promise((resolve) => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
      
      return refreshToken(retryCount + 1);
    }
    
    // Max retries reached, logout
    console.error('❌ Max refresh retries reached, logging out');
    isRefreshing = false;
    performLogout();
    return null;
  }
}

/**
 * Check token and refresh if needed
 */
async function checkAndRefreshToken(): Promise<void> {
  const token = secureStorage.getToken(userGlobalConfig.TOKEN);
  
  if (!token) {
    console.warn('⚠️  No token found during check');
    stopTokenMonitoring();
    return;
  }

  // Check if token is expired
  if (isTokenExpired(token)) {
    console.error('❌ Token expired during check');
    performLogout();
    return;
  }

  // Check if token needs refresh
  if (shouldRefreshToken(token)) {
    console.log('⏰ Token needs refresh');
    await refreshToken();
  }
}

/**
 * Start monitoring token expiration
 */
export function startTokenMonitoring(): void {
  // Clear any existing timer
  stopTokenMonitoring();
  
  console.log('👁️  Starting token monitoring');
  
  // Initial check
  checkAndRefreshToken();
  
  // Set up periodic checks
  refreshTimer = setInterval(() => {
    checkAndRefreshToken();
  }, TOKEN_CHECK_INTERVAL);
}

/**
 * Stop monitoring token expiration
 */
export function stopTokenMonitoring(): void {
  if (refreshTimer) {
    console.log('🛑 Stopping token monitoring');
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Manual token refresh (can be called from interceptor)
 */
export async function manualRefreshToken(): Promise<string | null> {
  return refreshToken();
}

/**
 * Get time until token expiration (in milliseconds)
 */
export function getTimeUntilExpiration(): number | null {
  const token = secureStorage.getToken(userGlobalConfig.TOKEN);
  if (!token) {
    return null;
  }
  
  const expiration = getTokenExpiration(token);
  if (!expiration) {
    return null;
  }
  
  return expiration - Date.now();
}

/**
 * Check if user is authenticated with valid token
 */
export function isAuthenticated(): boolean {
  const token = secureStorage.getToken(userGlobalConfig.TOKEN);
  if (!token) {
    return false;
  }
  
  return !isTokenExpired(token);
}